import { memo, useId } from 'react'
import styled from 'styled-components'

import { DomUtil } from '@root/shared'

import {
  BAR_GAP,
  BAR_WIDTH,
  CHART_HEIGHT,
  PADDING_INLINE,
  PADDING_TOP,
} from './constants'
import { TradePositionChartSwaps } from './trade-position-chart-swaps'
import { useChartData } from './use-chart-data'

import type { ChartEntry } from './use-chart-data'
import type { WalletPosition } from '@root/entities/position'
import type { Trend } from '@root/entities/trend'

type TradePositionChartProps = {
  pnlType: Trend.Trend
  entries: Array<WalletPosition.ChartRawEntry>
}

export const TradePositionChart: React.FC<TradePositionChartProps> = memo(
  ({ entries: rawEntries }) => {
    const { entries, minBoundary, maxBoundary } = useChartData({ rawEntries })

    const count = entries.length

    const fillColor = `var(--pnl-color)`

    const gradientId = useId()

    const chartWidth =
      count * BAR_WIDTH + (count - 1) * BAR_GAP + PADDING_INLINE * 2

    const innerHeight = CHART_HEIGHT - PADDING_TOP

    const calculateBarStartCoordinates = (idx: number) => {
      const dataPoint = entries.at(idx)
      if (!dataPoint) {
        return undefined
      }

      const barHeight = dataPoint.price
        .minus(minBoundary)
        .dividedBy(maxBoundary.minus(minBoundary))
        .multipliedBy(innerHeight)
        .toNumber()

      const xStart = PADDING_INLINE + idx * BAR_WIDTH + idx * BAR_GAP
      const yStart = innerHeight - barHeight + PADDING_TOP

      return {
        barHeight,
        xStart,
        yStart,
      }
    }

    const renderBar = (entry: ChartEntry, idx: number) => {
      const position = calculateBarStartCoordinates(idx)
      if (!position) {
        return undefined
      }

      const { barHeight, xStart, yStart } = position
      return (
        <>
          <rect
            x={xStart}
            y={yStart}
            key={idx}
            height={barHeight}
            width={BAR_WIDTH}
            rx={2}
            data-price={entry.price.toString()}
          />
          <TradePositionChartSwaps
            barCenterX={xStart + BAR_WIDTH / 2}
            barOriginY={yStart}
            entry={entry}
          />
        </>
      )
    }

    return (
      <Root>
        <svg
          height={CHART_HEIGHT}
          width={chartWidth}
          viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
        >
          <g fill={DomUtil.svgUrl(gradientId)}>{entries.map(renderBar)}</g>

          <defs>
            <linearGradient
              id={gradientId}
              x1="50%"
              x2="50%"
              y1="0"
              y2={CHART_HEIGHT}
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor={fillColor} />
              <stop offset="1" stopColor={fillColor} stopOpacity={0.1} />
            </linearGradient>

            <defs>
              <linearGradient
                id="chart-line-gradient"
                x1="0.5"
                y1="0%"
                x2="0.5"
                y2="100%"
                gradientUnits="objectBoundingBox"
              >
                <stop stopColor="white" />
                <stop offset="1" stopColor="white" stopOpacity="0" />
              </linearGradient>
            </defs>
          </defs>
        </svg>
      </Root>
    )
  },
)

const Root = styled.div`
  display: grid;

  justify-items: center;
  align-items: end;
`
